import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

// Pages & Components
import Home from "./pages/Home";
import Games from "./pages/Games";
import GamePage from "./pages/GamePage";
import News from "./pages/News.js";
import ArticlePage from "./pages/ArticlePage";
import ErrorPage from "./pages/ErrorPage";
import Login from "./pages/Login";
import Navigation from "./components/ui/Navigation";
import Footer from "./components/ui/Footer";
import Careers from "./pages/other/Careers";
import CareerPage from "./pages/careers/CareerPage";
import Company from "./pages/other/Company";
import Privacy from "./pages/other/Privacy";
import Terms from "./pages/other/Terms";
import AgeGate from "./pages/AgeGate";
import ScrollToTop from "./components/utility/ScrollToTop";
import Contests from "./pages/other/Contest";
import Giveaways from "./pages/other/Giveaways";
import FAQ from "./pages/other/FAQ";
import Loading from "./components/Loading";
import CookieConsent from "./components/utility/CookieConsent.js";
import GamePageGuard from "./components/utility/GamePageGuard.js";

// Styles
import "./styles/app.scss";
import "react-quill/dist/quill.snow.css";

function App() {
  const [isLoading, setLoading] = useState(true);
  const [games, setGames] = useState([]);
  const [news, setNews] = useState([]);
  const [totalArticles, setTotalArticles] = useState(0);
  const [careers, setCareers] = useState([]);
  
  // Initialize state directly from localStorage to prevent flash of wrong data
  const [platform, setPlatform] = useState(() => localStorage.getItem("platform") || "");
  const [region, setRegion] = useState(() => localStorage.getItem("region") || "");
  const [token, setToken] = useState(() => Boolean(localStorage.getItem("login_token")));

  const setTokenWrapper = (val) => {
    setToken(val);
  };

  const changePlatform = (newPlatform) => {
    localStorage.setItem("platform", newPlatform);
    setPlatform(newPlatform);
  };

  const changeRegion = (newRegion) => {
    localStorage.setItem("region", newRegion);
    setRegion(newRegion);
  };

  // GAMES, NEWS, & CAREERS API REQUEST
  useEffect(() => {
    const fetchRequests = async () => {
      setLoading(true);
      const storedToken = localStorage.getItem("login_token");

      try {
        // 1. Fetch Games & News (Conditional on Authentication)
        if (storedToken) {
          try {
            const [gamesRes, newsRes] = await Promise.all([
              axios.get(`https://nisamerica.com/api/games-private/${storedToken}`),
              axios.get(`https://nisamerica.com/api/blogs-private/${storedToken}`)
            ]);
            setGames(gamesRes.data.games || []);
            setNews(newsRes.data.blogs || []);
            setTotalArticles(newsRes.data.blogs?.length || 0);
          } catch (error) {
            console.error("Private API failed, falling back to public endpoints:", error);
            await fetchPublicGamesAndNews();
          }
        } else {
          await fetchPublicGamesAndNews();
        }

        // 2. Fetch Careers (Always Public)
        const careersRes = await axios.get("https://nisamerica.com/api/careers");
        setCareers(careersRes.data.careers || []);

      } catch (error) {
        console.error("An error occurred while fetching initial data:", error);
      } finally {
        setTimeout(() => setLoading(false), 250);
      }
    };

    const fetchPublicGamesAndNews = async () => {
      const [gamesRes, newsRes] = await Promise.all([
        axios.get("https://www.nisamerica.com/api/games"),
        axios.get("https://nisamerica.com/api/test/blogs")
      ]);
      setGames(gamesRes.data.games || []);
      setNews(newsRes.data.blogs || []);
      setTotalArticles(newsRes.data.length || newsRes.data.blogs?.length || 0);
    };

    fetchRequests();
  }, [token]);

  if (isLoading) {
    return <div className="App"><Loading /></div>;
  }

  return (
    <AnimatePresence>
      <motion.div
        className="App"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.75 }}
      >
        <Router basename={process.env.PUBLIC_URL}>
          <CookieConsent />
          <Navigation />
          <ScrollToTop />
          <div className="app-wrapper">
            <Routes>
              {/* Core Routes */}
              <Route path="/" element={<Home games={games} news={news} />} />
              <Route path="/games" element={<Games games={games} />} />
              
              {/* Parameterized Dynamic Routes (Replaces .map()) */}
              <Route
                path="/games/:pathname"
                element={
                  <GamePageGuard
                    games={games}
                    platform={platform}
                    changePlatform={changePlatform}
                    region={region}
                    changeRegion={changeRegion}
                    token={token}
                  />
                }
              />

              <Route 
                path="/blog" 
                element={<News news={news} totalArticles={totalArticles} />} 
              />
              <Route
                path="/blog/:url"
                element={<ArticlePage articles={news} games={games} />}
              />

              <Route path="/careers" element={<Careers careers={careers} />} />
              <Route
                path="/careers/:url"
                element={<CareerPage careers={careers} />}
              />

              {/* Static / Other Routes */}
              <Route path="/company" element={<Company />} />
              <Route path="/privacy-policy" element={<Privacy />} />
              <Route path="/terms-of-use" element={<Terms />} />
              <Route path="/FAQ" element={<FAQ />} />
              <Route path="/age" element={<AgeGate />} />
              <Route path="/contests" element={<Contests />} />
              <Route path="/contest" element={<Navigate replace to="/contests" />} />
              <Route path="/giveaways" element={<Giveaways />} />
              <Route path="/nislogin" element={<Login token={token} setToken={setTokenWrapper} />} />
              <Route path="/games-us" element={<div className="games-container"><Navigate replace to="/games" /></div>} />
              
              {/* 404 Catch-All */}
              <Route path="/404" element={<ErrorPage />} />
              <Route path="*" element={<Navigate replace to="/404" />} />
            </Routes>
          </div>
          <Footer />
          
          {token && (
            <button
              className="home-button"
              style={{ marginBottom: "50px" }}
              onClick={() => {
                localStorage.removeItem("login_token");
                setToken(false);
              }}
            >
              <h1>LOGOUT</h1>
            </button>
          )}
        </Router>
      </motion.div>
    </AnimatePresence>
  );
}

export default App;